import React, { useState, useEffect } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Client } from "@/types";
import { apiClient } from "@/services/apiClient";

interface CaptionConfigModalProps {
  isOpen: boolean;
  client: Client;
  onClose: () => void;
  onSaved: () => void;
}

interface CaptionConfig {
  brandVoice: string;
  targetAudience: string;
  hashtags: string[];
  location: string;
  brandDescription: string;
  writingGuidelines: string;
}

interface CaptionConfigResponse {
  success: boolean;
  captionConfig: CaptionConfig;
}

const EMPTY_CONFIG: CaptionConfig = {
  brandVoice: "",
  targetAudience: "",
  hashtags: [],
  location: "",
  brandDescription: "",
  writingGuidelines: "",
};

export const CaptionConfigModal: React.FC<CaptionConfigModalProps> = ({
  isOpen,
  client,
  onClose,
  onSaved,
}) => {
  const [config, setConfig] = useState<CaptionConfig>(EMPTY_CONFIG);
  const [hashtagInput, setHashtagInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchConfig();
    }
  }, [isOpen, client.id]);

  const fetchConfig = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiClient.get<CaptionConfigResponse>(
        `/client/${client.id}/caption-config`
      );
      if (data.success && data.captionConfig) {
        setConfig(data.captionConfig);
        setHashtagInput((data.captionConfig.hashtags || []).join(", "));
      } else {
        setConfig(EMPTY_CONFIG);
        setHashtagInput("");
      }
    } catch (err) {
      console.error("Error fetching caption config:", err);
      setConfig(EMPTY_CONFIG);
      setHashtagInput("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    const hashtags = hashtagInput
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0)
      .map((tag) => (tag.startsWith("#") ? tag : `#${tag}`));

    const payload = { ...config, hashtags };

    try {
      const data = await apiClient.put<{ success: boolean; error?: string }>(
        `/client/${client.id}/caption-config`,
        payload
      );

      if (data.success) {
        onSaved();
        onClose();
      } else {
        setError(data.error || "Failed to save configuration");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Network error. Please try again."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setError(null);
      onClose();
    }
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isSaving}>
        Cancel
      </Button>
      <Button
        type="button"
        onClick={handleSave}
        disabled={isSaving || isLoading}
        className="flex items-center gap-2"
      >
        {isSaving ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Saving...
          </>
        ) : (
          "Save"
        )}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Caption Generation Configuration"
      size="lg"
      footer={footerContent}
    >
      <div className="p-6 space-y-5">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Brand Voice
              </label>
              <input
                type="text"
                value={config.brandVoice}
                onChange={(e) =>
                  setConfig({ ...config, brandVoice: e.target.value })
                }
                placeholder="e.g., Autentico, engaging, professionale"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
                disabled={isSaving}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Audience
              </label>
              <textarea
                value={config.targetAudience}
                onChange={(e) =>
                  setConfig({ ...config, targetAudience: e.target.value })
                }
                placeholder="Giovani professionisti 25-40 anni, appassionati di caffe di qualita..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
                disabled={isSaving}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                value={config.location}
                onChange={(e) =>
                  setConfig({ ...config, location: e.target.value })
                }
                placeholder="e.g., Milano centro"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
                disabled={isSaving}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hashtags
              </label>
              <textarea
                value={hashtagInput}
                onChange={(e) => setHashtagInput(e.target.value)}
                placeholder="#specialtycoffee, #milanocoffee, #coffeelovers"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors font-mono text-sm"
                disabled={isSaving}
              />
              <p className="mt-1 text-xs text-gray-500">
                Separate with commas. # symbol is optional.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Brand Description
              </label>
              <textarea
                value={config.brandDescription}
                onChange={(e) =>
                  setConfig({ ...config, brandDescription: e.target.value })
                }
                placeholder="Coffee bistro specializing in specialty coffee and authentic Italian experience..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
                disabled={isSaving}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Writing Guidelines
              </label>
              <textarea
                value={config.writingGuidelines}
                onChange={(e) =>
                  setConfig({ ...config, writingGuidelines: e.target.value })
                }
                placeholder="Parla come il barista che conosce i suoi clienti per nome. Condividi momenti veri..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
                disabled={isSaving}
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};
